// DisplayNameWithStatusIndicator.jsx
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import TextEllipsis from '../style/TextEllipsis.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import HighlightSubstring from '../component/HighlightSubstring.jsx';
import media from '../style/media.js';
import { color } from '../style/variables.js';

const OBSERVER_OPTIONS = { triggerOnce: true };

export const DisplayNameWithStatusIndicator = ({
  id = '',
  query = '',
  username = '',
  displayName = '',
  isDisplayNameEnabled = true,
  isUserFetching = false,
  isUserFetched = false,
  fetchUser,
  marginTop = 12,
  fontSize = 12,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);
  const timerRef = useRef();
  const usernameText = `@${username || ''}`;

  useEffect(() => {
    const isMissingName = !username || !displayName;
    const canFetch = !isUserFetched && !isUserFetching;

    if (id && isMissingName && inView && canFetch && !timerRef.current) {
      timerRef.current = setTimeout(() => fetchUser({ id }), 0);
    }
  }, [
    displayName,
    fetchUser,
    id,
    inView,
    isUserFetched,
    isUserFetching,
    username,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  // It should be either a display name or a username.
  // Show display name first and username as a fallback.
  const renderDisplayNameOrUsername = () => (
    <PrimaryText marginTop={marginTop} fontSize={fontSize}>
      <Username>
        {isDisplayNameEnabled && displayName ? (
          displayName
        ) : query ? (
          <HighlightSubstring substring={query}>
            {usernameText}
          </HighlightSubstring>
        ) : (
          usernameText
        )}
      </Username>
      <UserStatusIndicatorWrapper>
        <UserStatusIndicator
          userId={id}
          livestreamSize={12}
          shouldShowOnline
          shouldShowLiveStream
        />
      </UserStatusIndicatorWrapper>
    </PrimaryText>
  );

  // It should be a username or empty.
  const renderSecondaryText = () =>
    isDisplayNameEnabled ? (
      <SecondaryText>
        {displayName ? (
          query ? (
            <HighlightSubstring substring={query}>
              {usernameText}
            </HighlightSubstring>
          ) : (
            usernameText
          )
        ) : (
          ''
        )}
      </SecondaryText>
    ) : null;

  return (
    <StyledDisplayNameWithStatusIndicator ref={ref}>
      {renderDisplayNameOrUsername()}
      {renderSecondaryText()}
    </StyledDisplayNameWithStatusIndicator>
  );
};

DisplayNameWithStatusIndicator.propTypes = {
  id: PropTypes.string,
  query: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  isDisplayNameEnabled: PropTypes.bool,
  isUserFetching: PropTypes.bool,
  isUserFetched: PropTypes.bool,
  fetchUser: PropTypes.func.isRequired,
  marginTop: PropTypes.number,
  fontSize: PropTypes.number,
};

const StyledDisplayNameWithStatusIndicator = styled.div``;

// Currently, we have 2 different font sizes,
// and they map to different area height respectfully:
// If the font size is 12, area height should be 18;
// if the font size is 16, area height should be 24;
const PrimaryText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  margin-top: ${({ marginTop }) => `${marginTop}px`};
  font-size: 16px;
  line-height: 150%;
  ${media.mobile`
    font-size: 14px;
  `}
`;

const Username = styled.div`
  color: var(--font-color-primary-on-dark, ${color.white});
  ${TextEllipsis};
`;

const UserStatusIndicatorWrapper = styled.div`
  margin-left: 2px;
`;

const SecondaryText = styled.div`
  text-align: center;
  height: 18px;
  color: var(--font-color-secondary-on-dark, ${color.neutral['85_alpha_80']});
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: 0.012px;
  ${TextEllipsis};
  font-size: 14px;
  ${media.mobile`
    font-size: 12px;
  `}
`;

export default DisplayNameWithStatusIndicator;
